import { useAuthStore } from "../contexts/AuthContext";
import { useDashboardListDispatch } from "../contexts/DashboardListContext";
import { useSelectedDashboardStore } from "../contexts/SelectedDashboardContext";
import { useCallback, useEffect } from "react";
import { apiGet } from "../apiCalls/api";
import { DashboardSlotA } from "../contexts/DashboardSlots";
import { useHandleCubeFetch } from "../helpers/useHandleCubeFetch";

// this component handles loading dashboards into designated contexts so that they can be viewed and edited
export default function DashboardLoadingController() {
  const dashboardListDispatch = useDashboardListDispatch();
  const { selectedAccount } = useAuthStore();
  const dashboardADispatch = DashboardSlotA.useAssignedDashboardDispatch();
  const dashboardGlobalDataADispatch = DashboardSlotA.useDataDispatch();
  const { selectedDashboardId } = useSelectedDashboardStore();
  const dashboardAAssigned = DashboardSlotA.useAssignedDashboard();
  const { handleCubeFetch } = useHandleCubeFetch();

  // accepts an object of key value pairs of {dashLoaderLetter: dashboardIdToDispatch, ...} i.e. {A: 2, B: 3, ...}, dispatches each to the correct dispatch
  const dispatchDashboardIdsToLoaders = useCallback(
    async (dispatches) => {
      try {
        /**
         * Separate into 3 calls:
         * Dashboard -> Dash object with DashboardUserSettings
         * DashboardCharts
         * DashboardDataTables
         * Don't need to get dashboardCategories because it's not an immediate use
         *
         */
        const dashboardDispatch = async (dashId, idDispatch, dataDispatch) => {
          idDispatch({
            type: "setDashboardIdAndStartLoading",
            payload: dashId,
          });
          apiGet(
            `/dashboards/${dashId}`,
            async (data) => {
              try {
                dataDispatch({
                  type: "setAllDashboardData",
                  payload: { ...data },
                });
                idDispatch({ type: "stopLoading" });
                return;
              } catch (error) {
                console.error("Error fetching dashboard data:", error);
                return;
              }
            },
            (error) => {
              console.error("Error fetching dashboard data:", error);
              return;
            }
          );
        };
        const dashboardDataTablesDispatch = async (
          dashId,
          idDispatch,
          dataDispatch
        ) => {
          idDispatch({ type: "startDashboardDataTablesLoading" });
          apiGet(
            `/dashboards/${dashId}/datatables`, //new endpoint to get dashboard datatables
            async ({ dashboard }) => {
              try {
                dataDispatch({
                  type: "setDashboardDataTablesData",
                  payload: dashboard?.DashboardDataTables,
                });
                idDispatch({ type: "stopDashboardDataTablesLoading" });
                if (dashboard?.DashboardDataTables?.length > 0) {
                  await handleCubeFetch({ dashboard }); // Correct usage
                } else {
                  idDispatch({ type: "stopDashboardDataTablesLoading" });
                }
                return;
              } catch (error) {
                console.error("Error fetching dashboard dataTables:", error);
                return;
              }
            },
            (error) => {
              console.error("Error fetching dashboard dataTables:", error);
              return;
            }
          );
        };
        const dashboardChartsDispatch = async (
          dashId,
          idDispatch,
          dataDispatch
        ) => {
          idDispatch({ type: "startDashboardChartsLoading", payload: dashId });
          apiGet(
            `/dashboards/${dashId}/charts`, //dashboard charts endpoint
            async ({ DashboardCharts }) => {
              try {
                if (DashboardCharts?.length > 0) { 
                  dataDispatch({
                    type: "setDashboardChartsData",
                    payload: DashboardCharts,
                  });
                }else {
                  dataDispatch({
                    type: "setDashboardChartsData",
                    payload: {}, // this is to make sure there is no carryover dashboard charts from previous dashboard
                  });
                }
                idDispatch({ type: "stopDashboardChartsLoading" });
                return;
              } catch (error) {
                console.error("Error fetching dashboard charts:", error);
                return;
              }
            },
            (error) => {
              console.error("Error fetching dashboard charts:", error);
              return;
            }
          );
        };
        const dashboardCategoriesDispatch = async (
          dashId,
          idDispatch,
          dataDispatch
        ) => {
          idDispatch({
            type: "startDashboardCategoriesLoading",
            payload: dashId,
          });
          apiGet(
            `/dashboards/${dashId}/categories`, //dashboard categories endpoint
            async (data) => {
              try {
                if (data?.length > 0) {
                  dataDispatch({
                    type: "setDashboardCategoriesData",
                    payload: data,
                  });
                } else{
                  dataDispatch({
                    type: "setDashboardCategoriesData",
                    payload: [], // this is to make sure there is no carryover dashboard categories from previous dashboard
                  });
                }
                idDispatch({ type: "stopDashboardCategoriesLoading" });
                return;
              } catch (error) {
                console.error("Error fetching dashboard categories:", error);
                return;
              }
            },
            (error) => {
              console.error("Error fetching dashboard categories:", error);
              return;
            }
          );
        };
        const dashboardViewAndFilterDispatch = async (
          dashId,
          idDispatch,
          dataDispatch
        ) => {
          idDispatch({
            type: "startDashboardViewsFiltersLoading",
            payload: dashId,
          });
          apiGet(
            `/dashboards/${dashId}/views-filters`, //dashboard views and filters endpoint
            async (data) => {
              try {
                if(data && data.message){
                  dataDispatch({
                    type: "setViewsAndFilters",
                    payload: {
                      views: [],
                      filters: {},
                      activeFilters: {},
                      activeViewId: null,
                    },
                  });
                  idDispatch({ type: "stopDashboardViewsFiltersLoading" });
                  return;
                }else if (
                  data &&
                  (data.views ||
                    data.filters ||
                    data.activeFilters ||
                    data.activeViewId)
                ) {
                  dataDispatch({
                    type: "setViewsAndFilters",
                    payload: data,
                  });
                }
                idDispatch({ type: "stopDashboardViewsFiltersLoading" });
                return;
              } catch (error) {
                console.error(
                  "Error fetching dashboard views and filters:",
                  error
                );
                return;
              }
            },
            (error) => {
              console.error(
                "Error fetching dashboard views and filters:",
                error
              );
              return;
            }
          );
        };
        await Promise.all([
          dispatches.A &&
            dashboardDispatch(
              dispatches.A,
              dashboardADispatch,
              dashboardGlobalDataADispatch
            ),
          dispatches.A &&
            dashboardChartsDispatch(
              dispatches.A,
              dashboardADispatch,
              dashboardGlobalDataADispatch
            ),
          dispatches.A &&
            dashboardDataTablesDispatch(
              dispatches.A,
              dashboardADispatch,
              dashboardGlobalDataADispatch
            ),
          dispatches.A &&
            dashboardCategoriesDispatch(
              dispatches.A,
              dashboardADispatch,
              dashboardGlobalDataADispatch
            ),
          dispatches.A &&
            dashboardViewAndFilterDispatch(
              dispatches.A,
              dashboardADispatch,
              dashboardGlobalDataADispatch
            ),
        ]);
        // Update DashboardListContext after all dashboards are loaded
        dashboardListDispatch({
          type: "setIsLoadingDashboards",
          payload: false,
        }); //need to update this with new dispatch
      } catch (error) {
        console.error("Error loading dashboards:", error);
      }
    },
    [
      dashboardADispatch,
      dashboardGlobalDataADispatch,
      dashboardListDispatch,
      handleCubeFetch,
    ]
  );

  useEffect(() => {
    if (!selectedAccount?.id && selectedAccount?.name !== "Innovare") {
      console.error("Selected account ID is undefined");
      return;
    }

    // Create an AbortController instance
    const controller = new AbortController();
    const { signal } = controller;

    dashboardListDispatch({ type: "setIsLoadingDashboards", payload: true });
    dashboardADispatch({ type: "startLoading" });

    // Determine URL based on selected account
    let url = selectedAccount.id
      ? `/api/dashboards?account=${selectedAccount.id}`
      : `/api/dashboards/templates?isDraft=true`;

    fetch(url, { signal, credentials: "include" })
      .then((r) => {
        if (r.ok) {
          return r.json();
        } else {
          throw new Error("Problem getting dashboards");
        }
      })
      .then((data) => {
        // Set the dashboard list in context
        dashboardListDispatch({ type: "setDashboards", payload: data });
        const dashids = data?.Dashboards.map((dash) => dash.id);
        dashboardListDispatch({ type: "setDashboardIds", payload: dashids });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Error fetching or loading dashboards:", error);
        }
      })
      .finally(() => {
        dashboardListDispatch({
          type: "setIsLoadingDashboards",
          payload: false,
        });
        dashboardADispatch({ type: "stopLoading" });
      });

    // Cleanup function to abort fetch in case account is changed midway
    return () => {
      controller.abort();
    };
  }, [selectedAccount.id]);

  // this effect runs when a dashboard is selected and dispatches it to a loader if needed
  useEffect(() => {
    // don't do anything if there is no dash selected (selectedDashboardId === null)
    if (selectedDashboardId) {
      // iterate through the loaders to see if they are initializing and/or already have the selected dash
      const loaderAssignments = [dashboardAAssigned];
      const info = {
        alreadySelected: false,
        allLoaded: true,
      };
      loaderAssignments.forEach((assignment) => {
        if (assignment.id && assignment.id === selectedDashboardId)
          info.alreadySelected = true;
        if (assignment.isLoading) info.allLoaded = false;
      });
      // only set the dashboard if the loaders are not currently loading and the selected dashboard is not already selected
      if (info.allLoaded && !info.alreadySelected) {
        // decide which DashboardSlot to dispatch to
        let slot = "A";
        dispatchDashboardIdsToLoaders({ [slot]: selectedDashboardId });
      }
    }
  }, [selectedDashboardId, dashboardAAssigned, dispatchDashboardIdsToLoaders]);

  return null;
}
